import {
  IAddress,
  PaymentMethod,
  IItemWithProduct,
  ReviewRating,
  Coordinate,
  FlavorSelection,
  FrequencyUnit,
  UISubscriptionStatus,
  BasicCatalogImage,
  BasicCategory,
  IItemProduct,
} from "common/types";
import { OrderStatus, OrderItemStatus, OrderItemModificationType } from "common/types/orders";
import { IFulfillmentEvent, FulfillmentStatus } from "common/types/fulfillments";
import { ITracker, ITrackerDetail, TrackerStatus } from "common/types/shipping";
import { ClientVendorOrderItem } from "common/types/vendor-orders";
import { Optional } from "common/utils/types";
import { DiscountBreakdown } from "common/types/carts";

import { IClientReview } from "client/reviews/types";
import { ModalStatus } from "client/common/types";

/** Matches ClientOrderStatus */
export enum ClientOrderStatus {
  InProgress = "IN_PROGRESS",
  Canceled = "CANCELED",
  Completed = "COMPLETED",
}

/** Matches ClientOrderItemStatus */
export enum ClientOrderItemStatus {
  InProgress = "IN_PROGRESS",
  Canceled = "CANCELED",
  Completed = "COMPLETED",
}

/** Matches ClientOrderSerializer */
export interface IOrder {
  id: number;
  display_number: string;
  status: OrderStatus;
  total: number;
  subtotal: number;
  shipping: number;
  tax: number;
  handling_fee: number;
  discount: number;
  employee_savings: number;
  num_items: number;
  order_placed_time: string;
  shipping_address: Optional<IAddress>;
  pickup_address: Optional<IAddress>;
  client_note: string;
  trackers: IGroupedOrderItemTracker[];
  ungrouped_items: IOrderItem[];
  fulfillments: IFulfillment[];
  payment_method: PaymentMethod;
  is_urgent: boolean;
  urgency_fee: number;
  has_any_pickup: boolean;
  has_any_shipping: boolean;
  client_handling_fees: number;
  client_purchasing_fee: number;
  order_items: IOrderItem[];
  discount_breakdown: DiscountBreakdown[];
  can_update: boolean;
  shipping_address_modified: boolean;
  payment_method_modified: boolean;
  is_all_pickup: boolean;
  is_cancelable: boolean;
  discounted_subtotal: number;
  can_update_payment_method: boolean;
  refund_description: string;
}

/** Matches orders ClientCheckoutOrderItemItemSerializer */
export type CheckoutOrderItemItem = {
  id: number;
  name: string;
  msrp: number;
  product: IItemProduct;
  days_supply: number;
  duration_value: number;
  duration_unit: FrequencyUnit;
};

/** Matches orders ClientCheckoutOrderItemSerializer */
export type CheckoutOrderItem = {
  id: number;
  item: CheckoutOrderItemItem;
  quantity: number;
  unit_price: number;
  patient: Optional<IOrderItemPatient>;
  prescription_fill: Optional<IClientOrderItemPrescriptionFill>;
  amortized_discount: number;
  status: OrderItemStatus;
  needs_client_confirmation: boolean;
  needs_controlled_substance_resolution: boolean;
  shipping_method_display: string | null;
};

/** Matches ClientCheckoutOrderSerializer */
export type ClientCheckoutOrder = {
  id: number;
  display_number: string;
  total: number;
  shipping: number;
  tax: number;
  shipping_address: Optional<IAddress>;
  has_any_pickup: boolean;
  order_items: CheckoutOrderItem[];
  coordinates: Optional<Coordinate>;
  discounted_subtotal: number;
};

/** Matches orders ClientOrderListTrackerSerializer */
export interface ClientListOrderItemTracker {
  id: number;
  status: TrackerStatus;
  status_display: string;
  carrier: string;
  last_updated: Optional<string>;
}

/** Matches orders ClientOrderListProductLineSerializer */
export interface ClientListOrderItemProductLine {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  categories: BasicCategory[];
  primary_category_name: string;
  short_description: string;
}

/** Matches orders ClientOrderListProductSerializer */
export interface ClientListOrderItemProduct {
  id: number;
  name: string;
  product_line: ClientListOrderItemProductLine;
}

/** Matches orders ClientOrderListItemSerializer */
export interface ClientListOrderItemItem {
  id: number;
  name: string;
  msrp: number;
  full_name: string;
  image: Optional<BasicCatalogImage>;
  product: ClientListOrderItemProduct;
  days_supply: number;
  duration_value: number;
  duration_unit: FrequencyUnit;
}

/** Matches orders ClientOrderListOrderItemSerializer */
export interface ClientListOrderItem {
  id: number;
  item: ClientListOrderItemItem;
  quantity: number;
  subtotal: number;
  unit_price: number;
  is_pickup: boolean;
  patient: Optional<IOrderItemPatient>;
  subscription: Optional<IOrderItemSubscription>;
  is_canceled: boolean;
  reviews: IClientReview[];
  tracker: Optional<ClientListOrderItemTracker>;
  status: OrderItemStatus;
  fulfillment_status: Optional<FulfillmentStatus>;
  prescription_fill: Optional<IClientOrderItemPrescriptionFill>;
  flavors_display: Optional<string>;
  modification_type: Optional<OrderItemModificationType>;
  amortized_discount: number;
  needs_client_confirmation: boolean;
  needs_controlled_substance_resolution: boolean;
  was_approval_needed: boolean;
  is_approved: boolean;
  note_to_client: Optional<string>;
}

/** Matches orders ClientOrderListOrderSerializer */
export interface ClientListOrder {
  id: number;
  display_number: string;
  status: OrderStatus;
  total: number;
  order_placed_time: string;
  shipping_address: Optional<IAddress>;
  pickup_address: Optional<IAddress>;
  order_items: ClientListOrderItem[];
  has_any_shipping: boolean;
  is_cancelable: boolean;
}

/** matches orders ClientCanceledOrderSerializer */
export interface CanceledOrder extends IOrder {
  // ids of subscriptions which were canceled as part of their order item being canceled
  newly_canceled_subscription_ids: number[];
  // for order items which were just canceled, ids of their related subscriptions which were unaffected
  // (could be active, already canceled, or any other status)
  unaffected_subscription_ids: number[];
}

/** Matches orders ClientOrderTrackerSerializer */
export interface IGroupedOrderItemTracker extends ITracker {
  items: IOrderItem[];
}

/** Matches orders ClientOrderItemPatientSerializer */
interface IOrderItemPatient {
  id: number;
  name_display: string;
}

/** Matches orders ClientOrderItemSubscriptionSerializer */
export interface IOrderItemSubscription {
  id: number;
  frequency_display: string;
  visible_status: UISubscriptionStatus;
  next_order_date: string | null;
}

/** Matches orders ClientOrderItemTrackerSerializer */
export type ClientDetailOrderItemTracker = {
  id: number;
  status: TrackerStatus;
  status_display: string;
  /** ISO datetime, unlike the "time since" text on ITracker */
  last_updated: Optional<string>;
  shipment_id: Optional<number>;
  tracking_code: string;
  est_delivery_date: Optional<string>;
  carrier: string;
  tracker_details: ITrackerDetail[];
  tracking_url: Optional<string>;
  label_url: Optional<string>;
};

/** Matches orders ClientOrderItemCancelationNoteSerializer */
export interface IOrderItemCancelationNote {
  id: number;
  note: string;
  author: string;
}

/** Matches orders ClientOrderItemSerializer */
export interface IOrderItem {
  id: number;
  item: IItemWithProduct;
  quantity: number;
  price: number;
  subtotal: number;
  unit_price: number;
  vendor_order_item: Optional<ClientVendorOrderItem>;
  handling_fee: number;
  is_pickup: boolean;
  patient: Optional<IOrderItemPatient>;
  subscription: Optional<IOrderItemSubscription>;
  is_canceled: boolean;
  cancelation_note: Optional<IOrderItemCancelationNote>;
  reviews: IClientReview[];
  tracker: Optional<ClientDetailOrderItemTracker>;
  status: OrderItemStatus;
  needs_client_confirmation: boolean;
  needs_controlled_substance_resolution: boolean;
  was_approval_needed: boolean;
  is_approved: boolean;
  fulfillment_status: Optional<FulfillmentStatus>;
  prescription_fill: Optional<IClientOrderItemPrescriptionFill>;
  flavor_selection: Optional<FlavorSelection>;
  flavors_display: Optional<string>;
  modification_type: Optional<OrderItemModificationType>;
  is_user_cancelable: boolean;
  amortized_discount: number;
  note_to_client: Optional<string>;
}

/** matches orders ClientOrderItemPrescriptionFillSerializer */
export interface IClientOrderItemPrescriptionFill {
  id: number;
  is_approved: boolean;
  prescribed_by_name: Optional<string>;
  approved_at: Optional<string>;
}

/** Matches fulfillments ClientFulfillmentSerializer */
export interface IFulfillment {
  id: number;
  items: IOrderItem[];
  events: IFulfillmentEvent[];
  can_be_picked_up_by_client: boolean;
  is_self_ship: boolean;
}

export interface IOrderPaymentMutate {
  payment_method_id: number;
}

/** matches orders ClientOrderCancelView data */
export type OrderCancelMutate = {};

export type OrderSearch = {
  // tracking modal params
  trackingModalIsVisible?: ModalStatus;
  trackerId?: number;
  // review modal params
  reviewCreateModalIsVisible?: ModalStatus;
  productLineId?: number;
  initialRating?: ReviewRating;
  // Payment modal params
  paymentModalIsVisible?: ModalStatus;
  // Modifications modal params
  modificationsModalIsVisible?: ModalStatus;
  // Cancel modal params
  cancelationModalIsVisible?: ModalStatus;
};

/** Matches query params for GET /api/client/v1/orders/list/ */
export type OrdersSearch = {
  page?: number;
  page_size?: number;
};

/** Matches orders ClientOrderLookupDetailView query params */
export interface OrderLookupSearch {
  display_number: string;
}

/** Matches orders ClientOrderLookupSerializer */
export interface IOrderLookup extends IOrder {
  client_name: string;
}
